// src/components/explorers/wildfireExplorer/wildfireExplorer.tsx
// Main Wildfire Explorer Component using native UDL

import React, { useState, useEffect, useRef } from 'react';
import { useFusionAuth } from '@fusionauth/react-sdk';
import { extractUserInfo, UserContextInfo, DEFAULT_USER_CONTEXT } from '../../../utils/userContext';
import ConfigurableBaseExplorer from '../baseExplorer/ConfigurableBaseExplorer';
import ContextHelp from '../../../easymap/ContextHelp/ContextHelp';
import { createWildfireConfig } from './wildfireConfig';
import { WildfireEntity } from '../../../types/wildfire';
import { WildfireDetailModal } from './WildfireDetailModal';
import AppModal from '../../common/AppModal';
import WildfireStatsDashboard from '../../stats/WildfireStatsDashboard';
import { createPortal } from 'react-dom';
import './WildfireExplorer.scss';

/**
 * Props interface for Wildfire Explorer
 */
interface WildfireExplorerProps {
    className?: string;
    initialPosition?: { x: number; y: number };
    initialSize?: { width: number; height: number };
    onClose?: () => void;
    onSelectionChange?: (selection: WildfireEntity[]) => void;
    onDataChange?: () => void;
    isWindowed?: boolean;
    windowTitle?: string;
    resizable?: boolean;
    draggable?: boolean;

    // Optional filter overrides
    initialFilters?: Array<{
        field: string;
        operator: string;
        value: any;
    }>;

    // Optional region filter for regional views
    regionFilter?: string;
}

/**
 * Wildfire Explorer Component
 * 
 * This is a NEW explorer (not legacy migration) that uses:
 * - Native UDL API for database access
 * - PostgreSQL backend (after migration)
 * - ConfigurableBaseExplorer pattern like WSE/DLE
 * 
 * Features:
 * - Search across fire number, cause, region, status
 * - Filter by status, region, cause
 * - Sort by any column
 * - Export to CSV/Excel
 * - Windowed or embedded display
 * - Real-time data from UDL → PostgreSQL
 * - Role-based permissions (future)
 */
const WildfireExplorer: React.FC<WildfireExplorerProps> = (props) => {
    const { userInfo, isFetchingUserInfo, isLoggedIn } = useFusionAuth();
    const [userContext, setUserContext] = useState<UserContextInfo>(DEFAULT_USER_CONTEXT);
    const [selectedWildfire, setSelectedWildfire] = useState<WildfireEntity | null>(null);
    const [showStats, setShowStats] = useState(false);
    const minimizeRef = useRef<(() => void) | null>(null);

    // Mobile-aware default sizing
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    const defaultPosition = isMobile ? { x: 5, y: 5 } : { x: 100, y: 100 };
    const defaultSize = isMobile
        ? { width: window.innerWidth - 10, height: window.innerHeight - 10 }
        : { width: 1200, height: 700 };

    // Extract full user context for future role-based features
    useEffect(() => {
        if (!isFetchingUserInfo && userInfo) {
            const contextInfo = extractUserInfo(userInfo);
            setUserContext(contextInfo);
        }
    }, [userInfo, isFetchingUserInfo]);

    /**
     * Handle entity view/edit - shows wildfire details modal
     */
    const handleEntityView = (entity: WildfireEntity) => {
        setSelectedWildfire(entity);
    };

    /**
     * Handle wildfire selection changes
     */
    const handleEntitySelect = (entity: WildfireEntity) => {
        if (props.onSelectionChange) {
            props.onSelectionChange([entity]);
        }
    };

    // Get configuration
    const baseConfig = createWildfireConfig();

    // Wait for user context to load before rendering
    // This prevents authentication issues during initial render
    if (isFetchingUserInfo) {
        return <div className="wildfire-explorer-loading">Loading user context...</div>;
    }

    const statsToolbar = (
        <button
            className="btn-neutral btn-sm"
            onClick={() => setShowStats(true)}
            title="Wildfire Statistics"
        >
            📊 Stats
        </button>
    );
    const wildfireHelp = <ContextHelp title='Help with Wildfires' context='wildfire-help' />;

    return (
        <>
            <ConfigurableBaseExplorer<WildfireEntity>
                key="wildfire-explorer"
                config={baseConfig}

                // Authentication
                isAuthenticated={isLoggedIn}

                // Window management
                isWindowed={props.isWindowed ?? true}
                initialPosition={props.initialPosition ?? defaultPosition}
                initialSize={props.initialSize ?? defaultSize}
                resizable={props.resizable ?? true}
                draggable={props.draggable ?? true}
                showCloseButton={true}
                showMinimizeButton={true}
                windowTitle={props.windowTitle || 'Wildfire Explorer'}
                onWindowClose={props.onClose}
                minimizeRef={minimizeRef}

                // Event handlers
                onEntityEdit={handleEntityView}
                onEntitySelect={handleEntitySelect}

                // Styling
                className={`wildfire-explorer ${props.className || ''}`}

                // Stats toolbar
                customToolbar={statsToolbar}
                help={wildfireHelp}
            />

            {/* Detail Modal */}
            {selectedWildfire && createPortal(
                <WildfireDetailModal
                    wildfire={selectedWildfire}
                    onClose={() => setSelectedWildfire(null)}
                    onMinimizeExplorer={() => minimizeRef.current?.()}
                />,
                document.body
            )}

            {/* Stats Dashboard Modal */}
            <AppModal
                isOpen={showStats}
                onClose={() => setShowStats(false)}
                width="90vw"
                maxHeight="85vh"
            >
                <WildfireStatsDashboard />
            </AppModal>
        </>
    );
};

export default WildfireExplorer;